/* @jsxImportSource @emotion/react */

import React, { useRef } from 'react'
import * as Sentry from '@sentry/browser'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, useIntl } from 'react-intl'

import type { PopoverSelectOption } from '@slite/components'
import {
  Input,
  isValidEmail,
  Select,
  Snackbar,
  Textarea,
} from '@slite/components'
import { ChevronDownIcon, InfoIcon, QuestionIcon } from '@slite/icons'

import { ButtonSubmit } from '../../components/ButtonSubmit'
import { getRudderAnalytics } from '../../renderer/rudderstack'
import { track } from '../../utils/analytics'
import { readAttributionParams } from '../../utils/attribution'
import { readDemoSource } from '../ai-transformation/analytics'

function openCalendlyScheduler(inputData: {
  firstName: string
  lastName: string
  email: string
}) {
  const fullName = `${inputData.firstName} ${inputData.lastName}`
  const url = `https://calendly.com/d/372-j8p-tnc/slite-demo?name=${encodeURIComponent(fullName)}&email=${encodeURIComponent(inputData.email)}&hide_gdpr_banner=1`
  if (window.Calendly) {
    window.Calendly.initPopupWidget({ url })
  } else {
    // The widget script loads async; a submit can beat it on slow
    // connections. A plain tab keeps the booking possible.
    window.open(url, '_blank', 'noopener')
    Sentry.captureException(new Error('BookDemo - Calendly is not loaded'))
  }
}

enum referralKeys {
  SEARCH = 'search',
  SOCIAL = 'social',
  FRIEND = 'friend',
  AI_SERVICES = 'ai_services',
  SLITE = 'slite',
  OTHER = 'other',
}
const getReferralOptions = (
  intl: IntlShape
): Record<string, PopoverSelectOption> => ({
  [referralKeys.SEARCH]: {
    title: intl.formatMessage({
      id: 'super.bookDemo.referralOptions.search',
      defaultMessage: 'Search engine (Google, Bing, etc.)',
    }),
    key: referralKeys.SEARCH,
  },
  [referralKeys.SOCIAL]: {
    title: intl.formatMessage({
      id: 'super.bookDemo.referralOptions.social',
      defaultMessage: 'Social media',
    }),
    key: referralKeys.SOCIAL,
  },
  [referralKeys.FRIEND]: {
    title: intl.formatMessage({
      id: 'super.bookDemo.referralOptions.friend',
      defaultMessage: 'Friend or colleague',
    }),
    key: referralKeys.FRIEND,
  },
  [referralKeys.AI_SERVICES]: {
    title: intl.formatMessage({
      id: 'super.bookDemo.referralOptions.aiServices',
      defaultMessage: 'AI assistant (ChatGPT, Perplexity, Gemini, etc.)',
    }),
    key: referralKeys.AI_SERVICES,
  },
  [referralKeys.SLITE]: {
    title: intl.formatMessage({
      id: 'super.bookDemo.referralOptions.slite',
      defaultMessage: 'We use or have used Slite',
    }),
    key: referralKeys.SLITE,
  },
  [referralKeys.OTHER]: {
    title: intl.formatMessage({
      id: 'super.bookDemo.referralOptions.other',
      defaultMessage: 'Other',
    }),
    key: referralKeys.OTHER,
  },
})

export function BookDemoForm() {
  const intl = useIntl()
  const formRef = useRef<HTMLFormElement>(null)
  const [submitState, setSubmitState] = React.useState<
    'idle' | 'success' | 'error' | 'loading'
  >('idle')
  const [emailError, setEmailError] = React.useState<string | null>(null)
  const [referral, setReferral] = React.useState('')
  const [otherReferral, setOtherReferral] = React.useState('')
  const referralOptions = getReferralOptions(intl)

  const labelClassName =
    'font-size-7 padding-bottom-4 shade-05 font-weight-semibold block'

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setEmailError(null)
    if (formRef.current) {
      const formData = new FormData(formRef.current)
      const inputData = Object.fromEntries(formData.entries()) as {
        firstName: string
        lastName: string
        email: string
        companyName: string
        companySize: string
        message: string
        jobTitle: string
      }

      if (!isValidEmail(inputData.email)) {
        setEmailError(
          intl.formatMessage({
            id: 'slite.bookDemo.form.errors.email',
            defaultMessage: 'Please enter a valid email',
          })
        )
        return
      }

      setSubmitState('loading')

      // Add referral info
      const referralSource = referral === 'other' ? otherReferral : referral

      const viewedPageEvent = window.dataLayer?.findLast(
        (item) => item.event === 'Viewed a Page'
      )

      const demoSource = readDemoSource()
      const data: Record<string, string | null> = {
        ...inputData,
        referralSource,
        pagePath: window.location.pathname,
        fromPagePath: (viewedPageEvent?.referrer as string) || null,
        initialReferrer: (viewedPageEvent?.initial_referrer as string) || null,
        anonymousId:
          (viewedPageEvent?.anonymousId as string) ||
          getRudderAnalytics()?.getAnonymousId() ||
          null,
        userId: (viewedPageEvent?.userId as string) || null,
        ...readAttributionParams(),
        ...(demoSource
          ? { demoSource: demoSource.source, demoSourcePage: demoSource.page }
          : {}),
      }

      // Post data to server and then Zapier
      fetch('/website/api/book-demo', {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to submit form')
          }
          setSubmitState('success')
        })
        .catch((error) => {
          Sentry.captureException(error)
          setSubmitState('error')
        })

      // Open calendly
      // Extract the domain from the email address
      const emailDomain = inputData.email.slice(
        inputData.email.lastIndexOf('@') + 1
      )

      // List of domains to exclude
      const excludedDomains = [
        'mail.com',
        'gmail.com',
        'protonmail.com',
        'mail.ru',
        'yopmail.com',
        'qq.com',
        'example.com',
        'bigpond.com',
        'mailinator.com',
        'outlook.com',
      ]

      // Check if the email domain is not in the excludedDomains list
      const calendarEligibleSizes = ['51-200', '201-500', '500+']
      if (
        !excludedDomains.includes(emailDomain) &&
        calendarEligibleSizes.includes(inputData.companySize)
      ) {
        openCalendlyScheduler(inputData)
      }

      // Send analytics data to Rudderstack
      track({ eventName: 'WEBSITE_DEMO_BOOKED', eventData: data })
    }
  }

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      className="flexbox flexbox-column flexbox-gap-12"
    >
      <div className="grid-12 grid-gap-16 ">
        <div className="grid-span-6-not-small grid-span-12">
          <label htmlFor="firstName" className={labelClassName}>
            <FormattedMessage
              id="bookdemo.firstname"
              defaultMessage="First Name *"
            />
          </label>
          <Input
            id="firstName"
            name="firstName"
            type="text"
            required
            placeholder="Roger"
          />
        </div>
        <div className="grid-span-6-not-small grid-span-12">
          <label htmlFor="lastName" className={labelClassName}>
            <FormattedMessage
              id="bookdemo.lastname"
              defaultMessage="Last Name *"
            />
          </label>

          <Input
            id="lastName"
            name="lastName"
            type="text"
            required
            placeholder="Dat"
          />
        </div>
      </div>

      <div className="grid-12 grid-gap-16 ">
        <div className="grid-span-6-not-small grid-span-12">
          <label
            htmlFor="email"
            className={`${labelClassName} flexbox flexbox-align-center flexbox-space-between`}
          >
            <FormattedMessage
              id="bookdemo.email"
              defaultMessage="Work email *"
            />
            {emailError && (
              <span className="flexbox flexbox-align-center flexbox-column-gap-4 error-content">
                <InfoIcon /> {emailError}
              </span>
            )}
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            placeholder="roger@acme.inc"
            aria-invalid={!!emailError}
          />
        </div>
        <div className="grid-span-6-not-small grid-span-12">
          <label htmlFor="jobTitle" className={labelClassName}>
            <FormattedMessage
              id="bookdemo.jobtitle"
              defaultMessage="Job Title *"
            />
          </label>
          <Input id="jobTitle" name="jobTitle" type="text" />
        </div>
      </div>

      <div className="grid-12 grid-gap-16 ">
        <div className="grid-span-6-not-small grid-span-12">
          <label htmlFor="companyName" className={labelClassName}>
            <FormattedMessage
              id="bookdemo.companyname"
              defaultMessage="Company Name *"
            />
          </label>
          <Input
            id="companyName"
            name="companyName"
            type="text"
            required
            placeholder="Acme Inc."
          />
        </div>
        <div className="grid-span-6-not-small grid-span-12">
          <label htmlFor="companySize" className={labelClassName}>
            <FormattedMessage
              id="bookdemo.companysize"
              defaultMessage="Company Size *"
            />
          </label>
          <div className="position-relative">
            <select
              id="companySize"
              name="companySize"
              required
              defaultValue={''}
              style={{
                height: '34px',
              }}
              css={{
                ':has(option:first-child:checked)': {
                  color: 'var(--shade-30)',
                },
              }}
              className="width-full  padding-horizontal-12 border-solid border-shade-40 background-transparent font-size-normal shade-00 flexbox-grow radius-large placeholder-shade-30"
            >
              <option value="" disabled>
                {intl.formatMessage({
                  id: 'bookdemo.companySize.select',
                  defaultMessage: 'Select...',
                })}
              </option>
              <option value="2-10">
                <FormattedMessage
                  id="bookdemo.employees.2-10"
                  defaultMessage="2-10 employees"
                />
              </option>
              <option value="11-50">
                <FormattedMessage
                  id="bookdemo.employees.11-50"
                  defaultMessage="11-50 employees"
                />
              </option>
              <option value="51-200">
                <FormattedMessage
                  id="bookdemo.employees.51-200"
                  defaultMessage="51-200 employees"
                />
              </option>
              <option value="201-500">
                <FormattedMessage
                  id="bookdemo.employees.201-500"
                  defaultMessage="201-500 employees"
                />
              </option>
              <option value="500+">
                <FormattedMessage
                  id="bookdemo.employees.500+"
                  defaultMessage="500+ employees"
                />
              </option>
            </select>
            <ChevronDownIcon
              size={12}
              className="position-absolute"
              style={{
                top: '50%',
                right: 16,
                transform: 'translateY(-50%)',
                color: 'var(--shade-05)',
              }}
            />
          </div>
        </div>
      </div>
      <div>
        <label htmlFor="message" className={labelClassName}>
          <FormattedMessage
            id="bookdemo.moredetails"
            defaultMessage="Provide more details (optional)"
          />
        </label>
        <Textarea
          id="message"
          name="message"
          style={{ height: 180, fontSize: 13 }}
          onChange={() => {}}
          className="placeholder-shade-30 "
        />
      </div>

      <Select
        placeholder={intl.formatMessage({
          id: 'super.bookDemo.form.referralPlaceholder',
          defaultMessage: 'How did you hear about us?',
        })}
        value={referral}
        onChange={(ref) => {
          setReferral(ref as unknown as referralKeys)
          // Reset otherReferral when switching away from "other"
          if (ref !== referralKeys.OTHER) {
            setOtherReferral('')
          }
        }}
        options={Object.values(referralOptions)}
      />
      {referral === referralKeys.OTHER && (
        <Input
          type="text"
          name="otherReferral"
          wrapperClassName="background-shade-100"
          placeholder={intl.formatMessage({
            id: 'super.bookDemo.form.otherReferralPlaceholder',
            defaultMessage: 'Please specify',
          })}
          required={true}
          value={otherReferral}
          onChange={(e) => setOtherReferral(e.target.value)}
        />
      )}

      {submitState === 'success' && (
        <div className="margin-bottom-16">
          <Snackbar>
            <FormattedMessage
              id="bookdemo.success"
              defaultMessage="Thank you, We'll get back to you shortly."
            />
          </Snackbar>
        </div>
      )}

      {submitState === 'error' && (
        <div className="margin-bottom-16">
          <Snackbar icon={QuestionIcon} variant="danger">
            <FormattedMessage
              id="bookdemo.error"
              defaultMessage="Something went wrong, try again or drop us an email at help@slite.com."
            />
          </Snackbar>
        </div>
      )}

      {submitState !== 'success' && (
        <ButtonSubmit disabled={submitState === 'loading'} variant="primary">
          {intl.formatMessage({
            id: 'bookdemo.submit',
            defaultMessage: 'Contact sales',
          })}
        </ButtonSubmit>
      )}
    </form>
  )
}
